import React, { useEffect, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputSwitch } from "primereact/inputswitch";
import { Tag } from "primereact/tag";
import './Panel.styles.scss';
import { getAllStrokes, getDetail, getRoutes, getStroke, getUnitPosition, Route } from "./Panel.service";
import { decrypt } from "../../utilities/Encriptacion";
import camion from "../../assets/img/camion.png";
import bus from "../../assets/img/bus.png";
import sprinter from "../../assets/img/sprinter.png";
import hiace from "../../assets/img/hiace.png";
import avanza from "../../assets/img/avanza.png"
import UnitDetailModal from "../../components/molecules/UnitDetail/UnitDetailModal";
import Map from "../../components/molecules/Map/Map";
import {Polyline, Marker, Popup} from "react-leaflet";
import L from "leaflet";
import markerImage from '../../assets/img/red_icon.png'
import Sidebar from "../../components/organisms/Sidebar/Sidebar";

interface Position {
    lat: number;
    lng: number;
}

interface IMarker extends Position {
    stop: string;
    time: string;
}

interface StrokeData {
    markers: { marker: IMarker }[];
    stroke: Position[];
    strokeColor: string;
    routeName: string
}

interface UnitPosition {
    position: Position,
    unit: string,
    id: number,
}

interface Detail {
    CHOFER: string
    UNIT: string
    OP_IMG: string
    UNIT_IMG: string
}

const Panel = () => {
    const [routes, setRoutes] = useState<Route[]>([]);
    const [name, setName] = useState<string | null>(null);
    const [unitId, setUnitId] = useState<number | null>(null);
    const [routeId, setRouteId] = useState<number | null>(null);
    const [routeIdTrazo, setRouteIdTrazo] = useState<number | null>(null);
    const [routeIdBitacora, setRouteIdBitacora] = useState<number | null>(null);
    const [getUnit, setGetUnit] = useState(true);
    const [strokes, setStrokes] = useState<StrokeData[]>([]);
    const [defaultCenter] = useState<Position>({ lat: 20.67129241646564, lng: -103.35232101509537 });
    const [unitPosition, setUnitPosition] = useState<UnitPosition | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [detail, setDetail] = useState<Detail | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showTime, setShowTime] = useState(true);
    const [selectedRoute, setSelectedRoute] = useState<{name: string, index: number} | null>(null);
    const [busType, setBusType] = useState<number | null>(3);
    const [isOn, setIsOn] = useState(true);
    const [isListVisible, setIsListVisible] = useState(false);
    const searchBoxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
                setIsListVisible(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const camionImages = {
        1: hiace,
        2: sprinter,
        3: camion,
        4: bus,
        6: camion,
        7: avanza
    };

    const getCamionIcon = (tipo: number | null) => {
        // @ts-ignore
        return camionImages[tipo] || camion;
    };

    const markerUnidad = new L.Icon({
        iconUrl: getCamionIcon(busType), // Reemplaza con tu imagen
        iconSize: [40, 22], // Tamaño del icono
        iconAnchor: [16, 25], // Punto de anclaje (centro en la base del ícono)
        popupAnchor: [0, -32], // Punto donde el popup aparece, relativo al ícono
    });

    const markerPersonalizado = (
        item: { marker: { time: string } },
        key: number,
        showTime: boolean
    ) => {
        return L.divIcon({
            className: "custom-marker",
            html: `
      <img style="width:40px;position:relative" src="${markerImage}" alt="marker"/>
      <div style="position:absolute;top:12px;left:${key < 10 ? "17px" : "14.5px"};font-weight:bold;font-size:0.6rem">${key}</div>
    `,
            iconSize: [25, 41], // Tamaño del icono
            iconAnchor: [20, 40], // Punto de anclaje
        });
    };

    useEffect(() => {
        const CLAVE = process.env.REACT_APP_CLAVE_ENCRYPTION;
        const dataClient = sessionStorage.getItem('dataClient');
        if (!dataClient) return;

        const clientId = JSON.parse(decrypt(dataClient, CLAVE)).id;

        const fetchRoutes = async () => {
            try {
                const data = await getRoutes(clientId, isOn);
                setRoutes(data);
            } catch (error) {
                console.error("Error fetching routes:", error);
            }
        };

        fetchRoutes();
    }, [isOn]);

    const resetSelection = () => {
        setSearchTerm('');
        setSelectedRoute(null);
        setIsListVisible(false);
    };

    const handleRouteClick = async (route: Route | null) => {
        resetSelection();

        if (!route) {
            setName("TODAS LAS RUTAS");
            setUnitId(null);
            setShowTime(false);
            setGetUnit(false);
            sessionStorage.removeItem('rutaId');

            try {
                const data = await getAllStrokes(isOn);
                setStrokes(data);
                setUnitPosition(null);
            } catch (error) {
                console.error("Error fetching all strokes:", error);
            }
            return;
        }

        setName(route.NOMBRE);
        setUnitId(route.ID_UNIDAD);
        setRouteId(route.ID);
        setRouteIdTrazo(route.ID_TRAZO);
        setRouteIdBitacora(route.ID_BITACORA);
        setGetUnit(true);
        setBusType(route.TIPO);
        if (route.ID) sessionStorage.setItem('rutaId', route.ID.toString());

        try {
            const data = await getStroke(route.ID_TRAZO);
            setStrokes(data);
        } catch (error) {
            console.error("Error fetching stroke:", error);
        }
    };

    useEffect(() => {
        if (!getUnit || !unitId) return;

        let cancelled = false;

        const fetchData = async () => {
            try {
                const position = await getUnitPosition(unitId, routeIdBitacora);
                if (cancelled) return;
                setUnitPosition(position);
                if (!position) setGetUnit(false);
            } catch (error) {
                console.error("Error fetching unit position:", error);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 10000);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [unitId, routeIdBitacora, getUnit]);


    const filteredRoutes = routes.filter(route =>
        route.NOMBRE?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const showDetail = async (id: number) => {
        try {
            const data = await getDetail(id);
            setDetail(data);
            setIsModalOpen(true);
        } catch (error) {
            console.error("Error fetching detail:", error);
        }
    };
    const closeModal = () => {
        setDetail(null);
        setIsModalOpen(false);
    }

    const handlePolylineClick = (routeName: string, index: number) => {
        setSelectedRoute({name: routeName, index: index});
        setName(routeName);
    }

    const displayedName = selectedRoute ? selectedRoute.name : name;

    return (
        <div className="panel-layout">
            { routeId && (
                <Sidebar
                    idBitacora={routeIdBitacora}
                    idTrazo={routeIdTrazo}
                />
            )}

            <div className="route-search-box" ref={searchBoxRef}>
                <div className="route-search-input">
                    <IconField iconPosition="left" className="route-search">
                        <InputIcon className="pi pi-search" />
                        <InputText
                            placeholder="Buscar ruta..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => setIsListVisible(true)}
                            onClick={() => setIsListVisible(true)}
                        />
                    </IconField>
                    <div className="route-type-switch">
                        <span>{isOn ? 'Entrada' : 'Salida'}</span>
                        <InputSwitch checked={isOn} onChange={(e) => setIsOn(!!e.value)} />
                    </div>
                </div>

                <ul className={`route-list ${isListVisible ? 'visible' : ''}`}>
                    <li
                        className={`route-list-item ${name === "TODAS LAS RUTAS" ? 'active' : ''}`}
                        onClick={() => handleRouteClick(null)}
                    >
                        <i className="pi pi-th-large" />
                        <span>TODAS LAS RUTAS</span>
                    </li>
                    {filteredRoutes.map((route, index) => (
                        <li
                            key={index}
                            className={`route-list-item ${route.NOMBRE === name ? 'active' : ''}`}
                            onClick={() => handleRouteClick(route)}
                        >
                            <i className="pi pi-directions" />
                            <span>{route.NOMBRE}</span>
                        </li>
                    ))}
                    {filteredRoutes.length === 0 && (
                        <li className="route-list-empty">Sin coincidencias</li>
                    )}
                </ul>
            </div>

            <main className="map-panel">
                <Map
                    center={defaultCenter}
                    zoom={10}
                    scrollWheelZoom={true}
                    style={{height: '100%', width: '100%', zIndex: "0"}}
                    mapStyle="positron"
                >
                    {strokes.map((route, routeIndex) => (
                        <React.Fragment key={routeIndex}>
                            <Polyline
                                pathOptions={{ color: route.strokeColor }}
                                positions={route.stroke.filter(p => p?.lat != null && p?.lng != null)}
                                eventHandlers={{
                                    click: () => handlePolylineClick(route.routeName, routeIndex),
                                }}
                            />

                            {(selectedRoute?.index === routeIndex || strokes.length === 1) &&
                                route.markers
                                    .filter(m => m?.marker?.lat != null && m?.marker?.lng != null)
                                    .map((marker, index) => (
                                        <Marker
                                            key={index}
                                            position={[marker.marker.lat, marker.marker.lng]}
                                            icon={markerPersonalizado(marker, index + 1, showTime)}
                                        >
                                            <Popup>
                                                <div className="info-window">
                                                    <h3>Parada {index + 1}</h3>
                                                    <p className="p-name">
                                                        <strong style={{ color: '#4285F4' }}>Nombre:</strong> {marker.marker.stop}
                                                    </p>
                                                    <p className="p-time">
                                                        <strong style={{ color: '#4285F4' }}>Hora de llegada:</strong> {marker.marker.time}
                                                    </p>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    ))
                            }
                        </React.Fragment>
                    ))}
                    <React.Fragment>
                        {unitPosition?.position?.lat != null && unitPosition?.position?.lng != null && (
                            <Marker
                                position={unitPosition.position}
                                icon={markerUnidad}
                                eventHandlers={{ click: () => showDetail(unitPosition.id) }}
                            />
                        )}
                    </React.Fragment>
                </Map>

                {displayedName && (
                    <Tag className="route-name-tag" icon="pi pi-map-marker" value={displayedName} />
                )}
            </main>

            <UnitDetailModal
                isOpen={isModalOpen}
                onClose={closeModal}
                driverPhoto={detail?.OP_IMG ?? ''}
                unitPhoto={detail?.UNIT_IMG ?? ''}
                driver={detail?.CHOFER ?? ''}
                unit={detail?.UNIT ?? ''}
            />
        </div>
    );
};

export default Panel;
